import { useEffect, useRef, useState } from "react";
import { Stage, Layer, Line, Rect, Circle, Text as KonvaText, Transformer } from "react-konva";
import { SHEET_W, SHEET_H, LAYER_ORDER, LAYER_STYLES, bakeNodeTransform, resetNode } from "./studioUtils";

const MARKUP_COLOR = "#ef4444";
const GHOST_COLOR = "#3b82f6";

function PrimShape({ el, common }) {
  const st = LAYER_STYLES[el.layer] || LAYER_STYLES.OUTLINE;
  const stroke = el.ghost ? GHOST_COLOR : el.layer === "MARKUP" ? MARKUP_COLOR : (el.stroke || st.color);
  const dash = el.dash ? (Array.isArray(el.dash) ? el.dash : String(el.dash).split(",").map(Number)) : st.dash;
  const base = {
    id: el.uid, name: el.uid, stroke, strokeWidth: el.sw || st.width,
    dash: dash || undefined, hitStrokeWidth: 10,
    opacity: el.ghost ? 0.55 : 1, listening: !el.auto && !el.ghost, ...common,
  };
  if (el.kind === "line") return <Line {...base} points={[el.x1, el.y1, el.x2, el.y2]} />;
  if (el.kind === "rect")
    return <Rect {...base} x={el.x} y={el.y} width={el.w} height={el.h}
      fill={el.fill && el.fill !== "none" ? el.fill : undefined} />;
  if (el.kind === "circle") return <Circle {...base} x={el.cx} y={el.cy} radius={el.r} />;
  if (el.kind === "poly")
    return <Line {...base} points={el.pts.flat()} closed={!!el.close}
      fill={el.close && el.fill && el.fill !== "none" ? el.fill : undefined} />;
  if (el.kind === "text") {
    const w = String(el.s).length * (el.size || 12) * 0.6;
    return <KonvaText {...base} x={el.x} y={el.y} text={String(el.s)} fontSize={el.size || 12}
      fill={stroke} stroke={undefined} rotation={el.rotate || 0}
      fontStyle={el.weight === "bold" ? "bold" : "normal"}
      offsetX={el.anchor === "middle" ? w / 2 : el.anchor === "end" ? w : 0} />;
  }
  return null;
}

export default function StudioCanvas({
  elements, ghosts, tool, activeLayer, layerVis, showGrid, locked,
  selection, setSelection, commit, onViewChange, onCursor, stageRef, canvasApi,
  pendingText, setPendingText, setTool, onMarkupPoint,
}) {
  const containerRef = useRef(null);
  const trRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [view, setView] = useState({ s: 0.5, x: 40, y: 40 });
  const [draft, setDraft] = useState(null);      // in-progress draw preview prim
  const [polyPts, setPolyPts] = useState([]);    // polyline accumulation
  const [dimPts, setDimPts] = useState([]);      // dimension clicks
  const [band, setBand] = useState(null);        // rubber-band select rect
  const panning = useRef(null);
  const spaceDown = useRef(false);
  const textDown = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // fit on first load of a drawing
  const fit = () => {
    const s = Math.min((size.w - 60) / SHEET_W, (size.h - 60) / SHEET_H);
    const v = { s, x: (size.w - SHEET_W * s) / 2, y: (size.h - SHEET_H * s) / 2 };
    setView(v);
    requestAnimationFrame(() => onViewChange && onViewChange(v));
  };
  useEffect(() => { if (size.w > 100) fit(); }, [elements === null]); // eslint-disable-line

  useEffect(() => {
    if (canvasApi) canvasApi.current = { fit, zoom: (f) => zoomAt({ x: size.w / 2, y: size.h / 2 }, f) };
  });

  // transformer attach
  useEffect(() => {
    const tr = trRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    const nodes = selection.map((id) => stage.findOne(`#${id}`)).filter(Boolean);
    tr.nodes(nodes);
    tr.getLayer() && tr.getLayer().batchDraw();
  }, [selection, elements]);

  // space key pan
  useEffect(() => {
    const dn = (e) => { if (e.code === "Space" && !/INPUT|TEXTAREA/.test(e.target.tagName)) { spaceDown.current = true; e.preventDefault(); } };
    const up = () => { spaceDown.current = false; };
    window.addEventListener("keydown", dn);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); };
  }, []);

  const toSheet = (pt) => ({ x: (pt.x - view.x) / view.s, y: (pt.y - view.y) / view.s });

  const zoomAt = (pt, factor) => {
    setView((v) => {
      const ns = Math.max(0.05, Math.min(20, v.s * factor));
      const nv = { s: ns, x: pt.x - ((pt.x - v.x) / v.s) * ns, y: pt.y - ((pt.y - v.y) / v.s) * ns };
      onViewChange && onViewChange(nv);
      return nv;
    });
  };

  const onWheel = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    const pt = stage.getPointerPosition();
    zoomAt(pt, e.evt.deltaY > 0 ? 1 / 1.12 : 1.12);
  };

  const isPanGesture = (e) =>
    tool === "pan" || spaceDown.current || e.evt.button === 1;

  const onMouseDown = (e) => {
    const stage = stageRef.current;
    const pt = toSheet(stage.getPointerPosition());
    if (isPanGesture(e)) {
      panning.current = { sx: e.evt.clientX, sy: e.evt.clientY, vx: view.x, vy: view.y };
      return;
    }
    if (locked) return;
    const clickedEmpty = e.target === stage || e.target.name() === "sheet";
    if (tool === "select") {
      if (clickedEmpty) {
        if (!e.evt.shiftKey) setSelection([]);
        setBand({ x: pt.x, y: pt.y, w: 0, h: 0 });
      }
      return;
    }
    if (tool === "line") {
      if (!draft) setDraft({ kind: "line", layer: activeLayer, x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y });
      else { commit([...elements, { ...draft, x2: pt.x, y2: pt.y, uid: undefined }]); setDraft(null); }
      return;
    }
    if (tool === "rect") {
      if (!draft) setDraft({ kind: "rect", layer: activeLayer, x: pt.x, y: pt.y, w: 0, h: 0 });
      else {
        const r = { ...draft, w: pt.x - draft.x, h: pt.y - draft.y };
        if (Math.abs(r.w) > 2 && Math.abs(r.h) > 2)
          commit([...elements, { ...r, w: Math.abs(r.w), h: Math.abs(r.h), x: Math.min(r.x, pt.x), y: Math.min(r.y, pt.y), uid: undefined }]);
        setDraft(null);
      }
      return;
    }
    if (tool === "circle") {
      if (!draft) setDraft({ kind: "circle", layer: activeLayer, cx: pt.x, cy: pt.y, r: 0 });
      else {
        const r = Math.hypot(pt.x - draft.cx, pt.y - draft.cy);
        if (r > 2) commit([...elements, { ...draft, r, uid: undefined }]);
        setDraft(null);
      }
      return;
    }
    if (tool === "polyline") {
      setPolyPts((p) => [...p, [pt.x, pt.y]]);
      return;
    }
    if (tool === "text") {
      textDown.current = pt;  // commit on mouseup only if it was a true click (not a drag)
      return;
    }
    if (tool === "dim") {
      const np = [...dimPts, pt];
      setDimPts(np);
      if (np.length === 3) {
        import("./studioUtils").then(({ buildLinearDim }) => {
          commit([...elements, ...buildLinearDim(np[0], np[1], np[2]).map((d) => ({ ...d, uid: undefined }))]);
        });
        setDimPts([]);
      }
      return;
    }
    if (tool === "markup_pin") {
      onMarkupPoint && onMarkupPoint(pt);
      return;
    }
    if (tool === "markup_circle") {
      if (!draft) setDraft({ kind: "circle", layer: "MARKUP", cx: pt.x, cy: pt.y, r: 0 });
      else {
        const r = Math.hypot(pt.x - draft.cx, pt.y - draft.cy);
        if (r > 2) commit([...elements, { ...draft, r, uid: undefined }]);
        setDraft(null);
      }
      return;
    }
    if (tool === "markup_arrow") {
      if (!draft) setDraft({ kind: "line", layer: "MARKUP", x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y });
      else {
        const a = draft;
        if (Math.hypot(pt.x - a.x1, pt.y - a.y1) > 2) {
          const ang = Math.atan2(pt.y - a.y1, pt.x - a.x1);
          const head = [[pt.x, pt.y],
            [pt.x - 10 * Math.cos(ang - 0.4), pt.y - 10 * Math.sin(ang - 0.4)],
            [pt.x - 10 * Math.cos(ang + 0.4), pt.y + 10 * Math.sin(ang + 0.4)]];
          commit([...elements,
            { ...a, x2: pt.x, y2: pt.y, uid: undefined },
            { kind: "poly", layer: "MARKUP", pts: head, close: true, fill: MARKUP_COLOR, uid: undefined }]);
        }
        setDraft(null);
      }
      return;
    }
  };

  const onMouseMove = (e) => {
    const stage = stageRef.current;
    const raw = stage.getPointerPosition();
    const pt = toSheet(raw);
    onCursor && onCursor(pt);
    if (panning.current) {
      const v = { ...view, x: panning.current.vx + e.evt.clientX - panning.current.sx, y: panning.current.vy + e.evt.clientY - panning.current.sy };
      setView(v);
      onViewChange && onViewChange(v);
      return;
    }
    if (band) { setBand({ ...band, w: pt.x - band.x, h: pt.y - band.y }); return; }
    if (draft) {
      if (draft.kind === "line") setDraft({ ...draft, x2: pt.x, y2: pt.y });
      else if (draft.kind === "rect") setDraft({ ...draft, w: pt.x - draft.x, h: pt.y - draft.y });
      else if (draft.kind === "circle") setDraft({ ...draft, r: Math.hypot(pt.x - draft.cx, pt.y - draft.cy) });
    }
  };

  const onMouseUp = (e) => {
    if (panning.current) { panning.current = null; return; }
    if (tool === "text" && textDown.current && !locked) {
      const stage = stageRef.current;
      const pt = toSheet(stage.getPointerPosition());
      const d = Math.hypot(pt.x - textDown.current.x, pt.y - textDown.current.y);
      textDown.current = null;
      if (d < 6 / view.s) setPendingText({ x: pt.x, y: pt.y, value: "" });
      return;
    }
    if (band) {
      const bx = Math.min(band.x, band.x + band.w), by = Math.min(band.y, band.y + band.h);
      const bw = Math.abs(band.w), bh = Math.abs(band.h);
      if (bw > 4 && bh > 4) {
        const hit = elements.filter((el) => {
          if (el.auto) return false;
          const c = el.kind === "circle" ? { x: el.cx, y: el.cy }
            : el.kind === "rect" ? { x: el.x + el.w / 2, y: el.y + el.h / 2 }
            : el.kind === "line" ? { x: (el.x1 + el.x2) / 2, y: (el.y1 + el.y2) / 2 }
            : { x: el.x ?? el.pts?.[0]?.[0] ?? 0, y: el.y ?? el.pts?.[0]?.[1] ?? 0 };
          return c.x >= bx && c.x <= bx + bw && c.y >= by && c.y <= by + bh;
        }).map((el) => el.uid);
        setSelection(hit);
      }
      setBand(null);
    }
  };

  const onDblClick = () => {
    if (tool === "polyline" && polyPts.length > 1) {
      commit([...elements, { kind: "poly", layer: activeLayer, pts: polyPts, close: false, uid: undefined }]);
      setPolyPts([]);
    }
  };

  // Konva event handlers receive ONLY the event — resolve the element from the
  // node's id (each PrimShape sets id/name to el.uid).
  const elFromEvent = (e) => elements.find((p) => p.uid === e.target.id());

  const onShapeClick = (e) => {
    if (tool !== "select" || locked) return;
    const el = elFromEvent(e);
    if (!el) return;
    e.cancelBubble = true;
    if (e.evt.shiftKey) setSelection(selection.includes(el.uid) ? selection.filter((i) => i !== el.uid) : [...selection, el.uid]);
    else setSelection([el.uid]);
  };

  const onDragEnd = (e) => {
    const node = e.target;
    const el = elements.find((p) => p.uid === node.id());
    if (!el) return;
    let dx = 0, dy = 0;
    if (el.kind === "line" || el.kind === "poly") { dx = node.x(); dy = node.y(); node.position({ x: 0, y: 0 }); }
    const updated = elements.map((p) => {
      if (p.uid !== el.uid) return p;
      const q = { ...p };
      if (q.kind === "line") { q.x1 += dx; q.y1 += dy; q.x2 += dx; q.y2 += dy; }
      else if (q.kind === "poly") { q.pts = q.pts.map(([a, b]) => [a + dx, b + dy]); }
      else if (q.kind === "rect") { q.x = node.x(); q.y = node.y(); }
      else if (q.kind === "circle") { q.cx = node.x(); q.cy = node.y(); }
      else if (q.kind === "text") { q.x = node.x(); q.y = node.y(); }
      return q;
    });
    commit(updated);
  };

  const onTransformEnd = (e) => {
    const node = e.target;
    const el = elements.find((p) => p.uid === node.id());
    if (!el) return;
    const baked = bakeNodeTransform(el, node);
    resetNode(node);
    if (el.kind === "line" || el.kind === "poly") node.position({ x: 0, y: 0 });
    commit(elements.map((p) => (p.uid === el.uid ? baked : p)));
    setSelection([]);
  };

  const common = {
    draggable: tool === "select" && !locked,
    onClick: onShapeClick,
    onTap: onShapeClick,
    onDragEnd,
    onTransformEnd,
  };

  const gridLines = [];
  if (showGrid) {
    for (let x = 0; x <= SHEET_W; x += 50) gridLines.push(<Line key={`gv${x}`} points={[x, 0, x, SHEET_H]} stroke="#e5e7eb" strokeWidth={0.5} listening={false} />);
    for (let y = 0; y <= SHEET_H; y += 50) gridLines.push(<Line key={`gh${y}`} points={[0, y, SHEET_W, y]} stroke="#e5e7eb" strokeWidth={0.5} listening={false} />);
  }

  const draftEls = [];
  if (draft) draftEls.push({ ...draft, uid: "__draft", ghost: true });
  if (polyPts.length > 1) draftEls.push({ kind: "poly", layer: activeLayer, pts: polyPts, close: false, uid: "__poly", ghost: true });
  if (dimPts.length) draftEls.push({ kind: "circle", layer: "DIMENSION", cx: dimPts[dimPts.length - 1].x, cy: dimPts[dimPts.length - 1].y, r: 3, uid: "__dim", ghost: true });

  const cursorStyle = tool === "pan" ? "grab" : tool === "select" ? "default" : "crosshair";

  return (
    <div ref={containerRef} className="absolute inset-0 bg-[var(--c-bg)] overflow-hidden" style={{ cursor: cursorStyle }} data-testid="studio-canvas">
      <Stage ref={stageRef} width={size.w} height={size.h} scaleX={view.s} scaleY={view.s} x={view.x} y={view.y}
        onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onDblClick={onDblClick}>
        <Layer listening={true}>
          <Rect name="sheet" x={0} y={0} width={SHEET_W} height={SHEET_H} fill="white" shadowColor="#000" shadowBlur={18} shadowOpacity={0.5} />
          {gridLines}
        </Layer>
        {LAYER_ORDER.map((ln) => (
          <Layer key={ln} name={ln} visible={layerVis[ln] !== false}>
            {elements.filter((el) => el.layer === ln).map((el) => <PrimShape key={el.uid} el={el} common={common} />)}
          </Layer>
        ))}
        <Layer listening={false}>
          {[...ghosts, ...draftEls].map((el) => <PrimShape key={el.uid} el={el} common={{}} />)}
        </Layer>
        <Layer>
          {band && <Rect x={band.x} y={band.y} width={band.w} height={band.h} fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth={1 / view.s} dash={[4, 3]} listening={false} />}
          <Transformer ref={trRef} rotateEnabled resizeEnabled anchorSize={7} anchorStroke="#3b82f6" borderStroke="#3b82f6" />
        </Layer>
      </Stage>
      {pendingText && (
        <input autoFocus data-testid="text-tool-input"
          className="absolute z-10 bg-[var(--c-surface)] border border-[var(--c-accent)] text-[var(--c-text)] text-xs px-2 py-1 rounded-sm outline-none"
          style={{ left: pendingText.x * view.s + view.x, top: pendingText.y * view.s + view.y }}
          value={pendingText.value}
          onChange={(e) => setPendingText({ ...pendingText, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter" && pendingText.value.trim()) {
              commit([...elements, { kind: "text", layer: tool === "text" ? activeLayer : "MARKUP", x: pendingText.x, y: pendingText.y, s: pendingText.value.trim(), size: 12, uid: undefined }]);
              setPendingText(null);
            } else if (e.key === "Escape") setPendingText(null);
          }}
          onBlur={() => setPendingText(null)} />
      )}
    </div>
  );
}
